<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :inputs="inputs"
    :moduleEnabler="moduleEnabler"
    :is-form-fetched="isFormFetched"
    @save="onSave"
  >
    <template v-for="input in inputs" #[`${input.name}.input`]="data">
      <v-col :key="input.name" cols="3">
        <data-input
          v-model="selectedDataSourceProps.settings[input.name]"
          v-bind="data"
          @change="input.name === 'layer_id' ? layerIdChanged() : setSettingsChanged()"
        />
      </v-col>
    </template>
  </admin-wrapper>
</template>
<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'FailuresTwoSettings',
  components: { AdminWrapper, DataInput },
  mixins: [prepareAttributesFeatureCardMixin],
  computed: {
    additionalModuleName: get('admin/additionalModules.FAILURES_2.verbose_name'),
    isModuleEnabled: get('admin/settingsValues@failures_2_module_enabled'),
    layers: get('layers/layers'),
    dataSources: get('layers/metadata'),
    dataSourceSettingsName() {
      return 'failures_2_datasource';
    },
    moduleEnabler() {
      return {
        name: 'failures_2_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    inputs() {
      return [
        {
          name: 'layer_id',
          translationPath: 'admin.failures.failuresLayer',
          dataType: {
            name: 'select',
          },
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          items: this.layersMapped,
        },
        {
          name: 'name_attribute',
          translationPath: 'admin.failures.failuresName',
          dataType: {
            name: 'select',
          },
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          items: this.selectedLayerMappedTextAttributesArray,
          disabled: !this.selectedLayerMetadata,
        },
        {
          name: 'description_attribute',
          translationPath: 'admin.failures.failuresDescription',
          dataType: {
            name: 'select',
          },
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          items: this.selectedLayerMappedTextAttributesArray,
          disabled: !this.selectedLayerMetadata,
        },
        {
          name: 'status_attribute',
          translationPath: 'admin.failures.failuresStatus',
          dataType: {
            name: 'select',
          },
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          items: this.selectedLayerMappedDictAttributesArray,
          disabled: !this.selectedLayerMetadata,
        },
        {
          name: 'deadline_attribute',
          translationPath: 'admin.failures.failuresDeadline',
          dataType: {
            name: 'select',
          },
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          items: this.selectedLayerMappedDateAttributesArray,
          disabled: !this.selectedLayerMetadata,
        },
        {
          name: 'owner_attribute',
          translationPath: 'admin.failures.failuresAssignedUser',
          dataType: {
            name: 'select',
          },
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          items: this.selectedLayerMappedRelatonalAttributesArray,
          disabled: !this.selectedLayerMetadata,
        },
        {
          name: 'reported_date_attribute',
          translationPath: 'admin.failures.failuresReportedDate',
          dataType: {
            name: 'select',
          },
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          items: this.selectedLayerMappedDateAttributesArray,
          disabled: !this.selectedLayerMetadata,
        },
        {
          name: 'address_attribute',
          translationPath: 'admin.failures.failureAddress',
          subtitle: 'admin.failures.failureAddressSubtitle',
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.selectedLayerMappedTextAttributesArray,
          disabled: !this.selectedLayerMetadata,
          clearable: true,
        },
        {
          name: 'status_new_value',
          translationPath: 'admin.failures.newValue',
          dataType: {
            name: 'select',
          },
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          items: this.uniqueStatusAttributeValues,
          disabled: !this.uniqueStatusValuesFetched,
        },
        {
          name: 'status_active_value',
          translationPath: 'admin.failures.activeValue',
          dataType: {
            name: 'select',
          },
          rules: [v => !!v || v == '0' || 'rules.required'],
          align: 'start',
          items: this.uniqueStatusAttributeValues,
          disabled: !this.uniqueStatusValuesFetched,
        },
      ];
    },
    layersArray() {
      return this.$_objectToArray(this.layers);
    },
    layersFiltered() {
      return this.layersArray.filter(layer => layer.type === 'features_layer' && layer.layer_scope === 'core');
    },
    layersMapped() {
      return this.layersFiltered.map(layer => {
        const { id, name } = layer;
        return { value: id, text: name };
      });
    },
    countsAttributesMapped() {
      return [];
    },
    selectedLayerMetadata() {
      return this.layers?.[this.selectedDataSourceProps.settings.layer_id] || undefined;
    },
    selectedLayerFormSchema() {
      return this.selectedLayerMetadata?.form_schema?.elements || [];
    },
    selectedLayerDataSourceName() {
      return this.selectedLayerMetadata?.data_source_name || '';
    },
    selectedLayerDataSourceMetadata() {
      return this.dataSources?.[this.selectedLayerDataSourceName] || undefined;
    },
    selectedLayerDataSourceAttributesSchema() {
      return this.selectedLayerDataSourceMetadata?.attributes_schema?.attributes || [];
    },
    selectedLayerAttributesArray() {
      return this.$_flattenStructure(
        this.getForm(this.selectedLayerFormSchema, this.selectedLayerDataSourceAttributesSchema, [], true)
      );
    },
    selectedLayerRelationalAttributesArray() {
      return this.selectedLayerAttributesArray.filter(attribute => attribute.isRelation && attribute.editable);
    },
    selectedLayerMappedRelatonalAttributesArray() {
      return this.selectedLayerRelationalAttributesArray.map(attribute => {
        const { attribute: value, label } = attribute;
        return { value, text: label };
      });
    },
    selectedLayerDictAttributesArray() {
      return this.selectedLayerAttributesArray.filter(attribute => attribute.isDict && attribute.editable);
    },
    selectedLayerMappedDictAttributesArray() {
      return this.selectedLayerDictAttributesArray.map(attribute => {
        const { attribute: value, label } = attribute;
        return { value, text: label };
      });
    },
    selectedLayerTextAttributesArray() {
      return this.selectedLayerAttributesArray.filter(attribute => attribute.dataType.name === 'text');
    },
    selectedLayerMappedTextAttributesArray() {
      return this.selectedLayerTextAttributesArray.map(attribute => {
        const { attribute: value, label } = attribute;
        return { value, text: label };
      });
    },
    selectedLayerDateAttributesArray() {
      return this.selectedLayerAttributesArray.filter(attribute =>
        ['date', 'time', 'datetime'].includes(attribute.dataType.name)
      );
    },
    selectedLayerMappedDateAttributesArray() {
      return this.selectedLayerDateAttributesArray.map(attribute => {
        const { attribute: value, label } = attribute;
        return { value, text: label };
      });
    },
  },
  data: () => ({
    isFormFetched: false,
    isSaving: false,
    selectedDataSourceProps: {
      settings: {
        layer_id: '',
        owner_attribute: '',
        status_attribute: '',
        status_new_value: '',
        status_active_value: '',
        deadline_attribute: '',
        reported_date_attribute: '',
        name_attribute: '',
        description_attribute: '',
        address_attribute: '',
      },
      datasource: '',
    },
    settingsChanged: false,
    uniqueStatusAttributeValues: [],
    uniqueStatusValuesFetched: false,
  }),
  methods: {
    getDatasourceSettings: call('datasourceSettings/getDatasourceSettings'),
    editDatasourceSettings: call('datasourceSettings/editDatasourceSettings'),
    getDataSources: call('layers/getLayersMetadata'),
    getLayers: call('layers/getLayers'),
    getModulesMapping: call('admin/getModulesMapping'),
    async init() {
      try {
        await Promise.all([
          this.getModulesMapping(),
          this.getDataSources({ with_attributes_schema: true }),
          this.getLayers(),
        ]);
        if (this.isModuleEnabled) {
          await this.updateSelectedLayer();
        }
        this.isFormFetched = true;
      } catch {
        //
      }
    },
    async onSave() {
      if (!this.settingsChanged) {
        return;
      }
      const payload = { name: this.dataSourceSettingsName, settings: this.selectedDataSourceProps };
      if (!payload.settings?.settings?.address_attribute) delete payload.settings?.settings?.address_attribute;
      try {
        await this.editDatasourceSettings(payload);
        this.settingsChanged = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
      } catch {
        //
      }
    },
    async updateSelectedLayer() {
      try {
        const r = await this.getDatasourceSettings(this.dataSourceSettingsName);
        const { datasource, settings } = r;
        this.selectedDataSourceProps = {
          datasource,
          settings,
        };
        this.settingsChanged = false;
      } catch {
        this.selectedDataSourceProps = {
          settings: {
            layer_id: '',
            owner_attribute: '',
            status_attribute: '',
            status_new_value: '',
            status_active_value: '',
            deadline_attribute: '',
            reported_date_attribute: '',
            name_attribute: '',
            description_attribute: '',
            address_attribute: '',
          },
          datasource: '',
        };
        this.settingsChanged = false;
      }
    },
    clearColumns() {
      this.selectedDataSourceProps.settings = {
        layer_id: this.selectedDataSourceProps.settings.layer_id,
        owner_attribute: '',
        status_attribute: '',
        status_new_value: '',
        status_active_value: '',
        deadline_attribute: '',
        reported_date_attribute: '',
        name_attribute: '',
        description_attribute: '',
        address_attribute: '',
      };
    },
    layerIdChanged() {
      this.clearColumns();
      this.selectedDataSourceProps.datasource = this.dataSources?.[this.selectedLayerDataSourceName]?.name || '';
    },
    setSettingsChanged() {
      this.settingsChanged = true;
    },
    getStatusAttributeDictValues(attributeName) {
      const attribute = this.selectedLayerDictAttributesArray.find(attr => attr.attribute === attributeName);
      this.uniqueStatusAttributeValues = attribute?.items || [];
      this.uniqueStatusValuesFetched = true;
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.init();
      }
    },
    'selectedDataSourceProps.settings.status_attribute': {
      handler(nV) {
        if (!nV) {
          this.uniqueStatusAttributeValues = [];
          this.uniqueStatusValuesFetched = false;
          return;
        }
        this.getStatusAttributeDictValues(nV);
      },
      immediate: true,
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>
